import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Plot from "react-plotly.js";
import { ArrowUp, ArrowDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import heartLoading from "../../../assests/heartLoading.gif";

const PAGE_SIZE = 20;

export const ProfilingResultView = ({ results }) => {
  const [currentRowCount, setCurrentRowCount] = useState("");
  const [graphOpen, setGraphOpen] = useState(false);
  const [graphColumn, setGraphColumn] = useState(null);
  const [statsPopupCol, setStatsPopupCol] = useState(null);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [columnSearch, setColumnSearch] = useState({
    column_name: "",
    type: "",
    isCde: "",
    businessterm: "",
    nullPercentage: "",
  });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const tableScrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const hasMoreRef = useRef(false);
  const totalFilteredRef = useRef(0);

  const scrollTable = (direction) => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({
        left: direction === "left" ? -200 : 200,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    setCurrentRowCount(results[0]?.count);
  }, []);

  const handleSort = (column) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleColumnSearchChange = (column, value) => {
    setColumnSearch((prev) => ({ ...prev, [column]: value }));
  };

  const filteredAndSortedData = useMemo(() => {
    if (!results || !Array.isArray(results)) return [];
    let filtered = [...results];

    Object.entries(columnSearch).forEach(([column, searchValue]) => {
      if (searchValue?.trim()) {
        filtered = filtered.filter((row) => {
          const val = column === "isCde" ? (row.isCde ? "yes" : "no") : (row[column] ?? "");
          return String(val).toLowerCase().includes(searchValue.toLowerCase().trim());
        });
      }
    });

    if (sortColumn) {
      filtered.sort((a, b) => {
        const aVal = sortColumn === "isCde" ? (a[sortColumn] ? "yes" : "no") : (a[sortColumn] ?? "");
        const bVal = sortColumn === "isCde" ? (b[sortColumn] ? "yes" : "no") : (b[sortColumn] ?? "");
        const cmp = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
        return sortOrder === "asc" ? cmp : -cmp;
      });
    }
    return filtered;
  }, [results, columnSearch, sortColumn, sortOrder]);

  // Reset visible count and update total ref whenever filtered set changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    totalFilteredRef.current = filteredAndSortedData.length;
  }, [filteredAndSortedData]);

  const currentData = filteredAndSortedData.slice(0, visibleCount);
  const hasMore = visibleCount < filteredAndSortedData.length;

  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  const handleIntersect = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMoreRef.current) {
      setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, totalFilteredRef.current));
    }
  }, []);

  useEffect(() => {
    const el = sentinelRef.current;
    const container = tableScrollRef.current;
    if (!el || !container) return;
    const observer = new IntersectionObserver(handleIntersect, {
      root: container,
      rootMargin: "100px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const StatisticsCell = ({ col }) => {
    const stats = [
      col.mean !== null && col.mean !== undefined ? { label: "Mean", value: col.mean } : null,
      col.max  !== null && col.max  !== undefined ? { label: "Max",  value: col.max  } : null,
      col.min  !== null && col.min  !== undefined ? { label: "Min",  value: col.min  } : null,
    ].filter(Boolean);

    if (stats.length === 0) return <span className="text-gray-400 text-[11px]">N/A</span>;

    const first = stats[0];
    const remaining = stats.length - 1;

    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[11px] text-gray-600">
          <span className="font-medium text-gray-700">{first.label}:</span> {first.value}
        </span>
        {remaining > 0 && (
          <button
            onClick={() => setStatsPopupCol(col)}
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-white text-[#5558E3] hover:bg-[#e0e7ff] transition-colors whitespace-nowrap cursor-pointer border border-[#5558E3]"
          >
            +{remaining} more
          </button>
        )}
      </div>
    );
  };

  const columns = [
    { key: "column_name",    label: "Column"        },
    { key: "type",           label: "Data Type"     },
    { key: "isCde",          label: "CDE"           },
    { key: "businessterm",   label: "Business Term" },
    { key: "nullPercentage", label: "Null %"        },
    { key: "statistics",     label: "Statistics",   noSort: true, noSearch: true },
    { key: "view",           label: "View",         noSort: true, noSearch: true },
  ];

  const SimpleStatCard = ({ title, value, color = "#1E88E5" }) => (
    <Box sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", padding: 3, width: "200px", height: "120px", display: "flex", flexDirection: "column", justifyContent: "space-between", border: "1px solid #d3d3d3", transition: "all 0.3s ease", cursor: "pointer", "&:hover": { boxShadow: "0 8px 25px rgba(0,0,0,0.15)", transform: "translateY(-4px)", border: "1px solid #bb1ccc" } }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color, fontSize: "1.1rem" }}>{title}</Typography>
        <Box sx={{ height: "3px", width: "40px", backgroundColor: color, borderRadius: "2px", mt: 0.5 }} />
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: "#3C3C3C", fontSize: "2rem" }}>{value.toLocaleString("en-US")}</Typography>
    </Box>
  );

  return (
    <div>
      {/* Stat cards */}
      <Box sx={{ display: "flex", gap: 4, mb: 2 }}>
        <SimpleStatCard title="Columns Analyzed" value={results?.length || 0} color="#5558E3" />
        <SimpleStatCard title="Rows Profiled"     value={currentRowCount || 0} color="#0F766E" />
      </Box>

      {results ? (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2 ml-2">
            <span className="text-sm text-gray-500">
              {Math.min(visibleCount, filteredAndSortedData.length)} of {filteredAndSortedData.length} result{filteredAndSortedData.length !== 1 ? "s" : ""}
              {results && filteredAndSortedData.length !== results.length && (
                <span className="text-gray-400 ml-2">(filtered from {results.length} total)</span>
              )}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => scrollTable("left")}
                className="flex items-center justify-center w-[56px] h-7 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-colors shadow-sm"
                title="Scroll left"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => scrollTable("right")}
                className="flex items-center justify-center w-[56px] h-7 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-colors shadow-sm"
                title="Scroll right"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg">
            <div
              ref={tableScrollRef}
              className="overflow-x-auto rounded-lg"
              style={{ overflowY: "auto", maxHeight: "560px" }}
            >
              <table className="w-full">
                <thead
                  className="text-[#404040] font-normal"
                  style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#F0FDFA" }}
                >
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        onClick={() => !col.noSort && handleSort(col.key)}
                        className={`px-3 pb-[6px] pr-[2px] text-[12px] pt-[10px] text-left whitespace-nowrap w-[14%] select-none font-normal ${!col.noSort ? "cursor-pointer" : ""}`}
                      >
                        <span className="flex items-center gap-1">
                          {col.label}
                          {!col.noSort && (
                            sortColumn === col.key
                              ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                              : <ArrowUp size={12} className="opacity-30" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {columns.map((col) => (
                      <th key={`search-${col.key}`} className="px-3 pt-0 pb-[10px] font-normal">
                        {!col.noSearch ? (
                          <input
                            className="w-full text-[11px] pb-[5px] pl-[10px] placeholder-gray-400 bg-white rounded py-2 outline-none font-normal placeholder:font-normal"
                            placeholder={col.key === "isCde" ? "Yes/No" : "Search"}
                            value={columnSearch[col.key] || ""}
                            onChange={(e) => handleColumnSearchChange(col.key, e.target.value)}
                          />
                        ) : (
                          <div className="h-[34px]" />
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 bg-white">
                  {currentData.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="py-8 text-center text-gray-500">
                        No data found matching your search criteria
                      </td>
                    </tr>
                  ) : (
                    currentData.map((col, index) => (
                      <tr key={col.column_name || index} className="hover:bg-gray-50 transition-colors">
                        <td className="py-2 px-3 text-[11px]">
                          <div className="font-medium text-gray-900">{col.column_name}</div>
                        </td>
                        <td className="py-2 px-3 text-[11px] text-gray-600">{col.type}</td>
                        <td className="py-2 px-3 text-[11px]">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${col.isCde ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                            {col.isCde ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-[11px] text-gray-600">{col.businessterm ?? "N/A"}</td>
                        <td className="py-2 px-3 text-[11px] text-gray-600">
                          {col.nullPercentage === null || col.nullPercentage === undefined
                            ? "N/A"
                            : `${col.nullPercentage || 0}%`}
                        </td>
                        <td className="py-2 px-3 text-[11px]">
                          <StatisticsCell col={col} />
                        </td>
                        <td className="py-2 px-3 text-[11px]">
                          <button
                            onClick={() => { setGraphOpen(true); setGraphColumn(col); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#5558E3] text-[#5558E3] bg-white text-[11px] font-medium hover:bg-[#eef2ff] transition-colors whitespace-nowrap"
                          >
                            View Plot
                          </button>
                        </td>
                      </tr>
                    ))
                  )}

                  {/* Sentinel inside the scrollable container */}
                  <tr>
                    <td colSpan={columns.length} className="p-0">
                      <div ref={sentinelRef} style={{ height: 1 }} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div className="flex items-center justify-center py-4 gap-2 border-t border-gray-100">
                <img src={heartLoading} alt="Loading more..." style={{ width : 200}} />
                <span className="text-gray-500 text-2xl">Loading more...</span>
              </div>
            )}
            {!hasMore && filteredAndSortedData.length > 0 && (
              <div className="text-center py-3 text-gray-400 text-xs border-t border-gray-100">
                All {filteredAndSortedData.length} rows loaded
              </div>
            )}
          </div>

          {statsPopupCol && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm" style={{ zIndex: 9999 }}>
              <div className="bg-white w-[400px] max-w-[92%] rounded-2xl shadow-2xl border border-gray-100">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div>
                    <h3 className="text-[14px] font-semibold text-gray-800">Statistics</h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">{statsPopupCol.column_name}</p>
                  </div>
                  <button onClick={() => setStatsPopupCol(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X size={18} />
                  </button>
                </div>
                <div className="px-5 py-4 flex flex-col gap-2">
                  {[
                    { label: "Mean", value: statsPopupCol.mean },
                    { label: "Max",  value: statsPopupCol.max  },
                    { label: "Min",  value: statsPopupCol.min  },
                  ]
                    .filter((s) => s.value !== null && s.value !== undefined)
                    .map((s, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5 bg-gray-50 rounded-lg border border-gray-100">
                        <span className="text-[12px] font-medium text-gray-500">{s.label}</span>
                        <span className="text-[13px] font-semibold text-gray-800">{s.value}</span>
                      </div>
                    ))}
                </div>
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
                  <span className="text-[11px] text-gray-400">
                    TOTAL ITEMS:{" "}
                    {[statsPopupCol.mean, statsPopupCol.max, statsPopupCol.min].filter(
                      (v) => v !== null && v !== undefined
                    ).length}
                  </span>
                  <button
                    onClick={() => setStatsPopupCol(null)}
                    className="px-4 py-1.5 text-[12px] font-medium bg-[#5558E3] text-white rounded-lg hover:bg-[#5558E3] transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          <Dialog open={graphOpen} onClose={() => setGraphOpen(false)} maxWidth="md" fullWidth sx={{ zIndex: 9999 }}>
            <div className="flex justify-end items-center p-4">
              <IconButton onClick={() => setGraphOpen(false)}><CloseIcon /></IconButton>
            </div>
            <DialogContent sx={{ p: 0 }}>
              <Plot
                data={(graphColumn?.chart?.data || []).map((trace) => ({
                  ...trace,
                  marker: { ...trace.marker, color: "#5558E3" },
                  line:   { ...trace.line,   color: "#5558E3" },
                }))}
                layout={{
                  ...(graphColumn?.chart?.layout || {}),
                  margin: { t: 60, b: 80, ...(graphColumn?.chart?.layout?.margin || {}) },
                  autosize: true,
                  width: null,
                  height: 500,
                }}
                style={{ width: "100%", height: "100%" }}
                useResizeHandler={true}
                config={{ displayModeBar: false }}
              />
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <p>No result to display</p>
      )}
    </div>
  );
};
